import React, { useState, useEffect } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { useObjets, useUpdateObjet, useDeleteObjet, useCreateObjet, useSyncObjetPhotos } from '../hooks/useObjets';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import { photoPayloadForSave } from '../utils/photoStorage';
import { usePermissions } from '../hooks/usePermissions';
import { useAuditActions } from '../hooks/useAudit';
import { useAuth } from '../hooks/useAuth';
import { useToastContext } from '../contexts/ToastContext';
import { useCreateItemMovement } from '../hooks/useMovements';
import ObjetEditModal from '../components/ObjetEditModal';
import ObjetCardMobile from '../components/ObjetCardMobile';
import PaginationControls from '../components/PaginationControls';
import ViewModeControls from '../components/ViewModeControls';
import AdvancedFilters from '../components/AdvancedFilters';
import CreateObjectButton from '../components/CreateObjectButton';
import ImageRecognitionModal from '../components/ImageRecognitionModal';
import ResponsiveGrid from '../components/ResponsiveGrid';
import { generateSKU } from '../utils/skuGenerator';
import MobileHeader from '../components/MobileHeader';
import MobileSearch from '../components/MobileSearch';
import SelectionControls from '../components/SelectionControls';
import BulkActions from '../components/BulkActions';
import BulkUpdateModal from '../components/BulkUpdateModal';
import BulkMoveModal from '../components/BulkMoveModal';
import BulkDeleteModal from '../components/BulkDeleteModal';
import BulkInventoryModal from '../components/BulkInventoryModal';
import MoveObjetModal from '../components/MoveObjetModal';
import SortGroupControls from '../components/SortGroupControls';
import ObjetListTable from '../components/ObjetListTable';
import { useInventaireViewMode } from '../hooks/useInventaireViewMode';
import { InventaireViewMode } from '../utils/inventaireViewMode';
import { sortItems } from '../utils/sortingUtils';
import { groupItems } from '../utils/groupingUtils';
import { isBulkPhotoLieuEnabled } from '../utils/featureEnv';
import {
  clearPendingRecognitionPhoto,
  stashPendingRecognitionPhoto,
  takePendingRecognitionPhoto,
} from '../utils/pendingRecognitionPhoto';

const InventairePage: React.FC = () => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const permissions = usePermissions();
  const auditActions = useAuditActions();
  const { user } = useAuth();
  const { showSuccess, showError, showWarning } = useToastContext();
  const createMovement = useCreateItemMovement();
  const [filters, setFilters] = useState({
    search: '',
    category: '',
    sousCategorie: '',
    status: '',
    couleurPrincipale: '',
    couleurSecondaire: '',
    besoinAchat: '',
    sansPhoto: '',
    sansLieu: '',
    ficheStatut: '',
  });
  
  const [showFilters, setShowFilters] = useState(false);
  const [inventoryMode, setInventoryMode] = useState(false);
  const [editInitialSection, setEditInitialSection] = useState<'infos' | 'localisation' | 'photos' | 'historique'>('infos');
  const [viewMode, setViewMode] = useInventaireViewMode();
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  const [editingObjet, setEditingObjet] = useState<any>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isSavingObjet, setIsSavingObjet] = useState(false);
  
  // Tri et groupement
  const [sortBy, setSortBy] = useState('nom-asc');
  const [groupBy, setGroupBy] = useState('none');
  
  const [movingObjet, setMovingObjet] = useState<any>(null);
  const [isMoveModalOpen, setIsMoveModalOpen] = useState(false);
  
  // Photo en cours de reconnaissance IA (non null = modal suggestions ouvert)
  const [recognitionPhoto, setRecognitionPhoto] = useState<string | null>(null);
  const [imageSuggestion, setImageSuggestion] = useState<any>(null);

  // États pour les opérations en masse
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isBulkUpdateOpen, setIsBulkUpdateOpen] = useState(false);
  const [isBulkMoveOpen, setIsBulkMoveOpen] = useState(false);
  const [isBulkDeleteOpen, setIsBulkDeleteOpen] = useState(false);
  const [isBulkInventoryOpen, setIsBulkInventoryOpen] = useState(false);

  // Appliquer les filtres depuis l'URL au chargement
  useEffect(() => {
    const filterParam = searchParams.get('filter');
    if (filterParam) {
      const [key, value] = filterParam.split(':');
      switch (key) {
        case 'etat':
          setFilters(prev => ({ ...prev, status: value }));
          break;
        case 'besoinAchat':
          setFilters(prev => ({ ...prev, besoinAchat: value }));
          break;
        case 'sansPhoto':
          setFilters(prev => ({ ...prev, sansPhoto: value }));
          setInventoryMode(value === 'true');
          setShowFilters(true);
          break;
        case 'sansLieu':
          setFilters(prev => ({ ...prev, sansLieu: value }));
          setShowFilters(true);
          break;
        case 'ficheStatut':
          setFilters(prev => ({ ...prev, ficheStatut: value }));
          setShowFilters(true);
          break;
        default:
          break;
      }
    }
  }, [searchParams]);

  const debouncedSearch = useDebouncedValue(filters.search, 300);

  // Utiliser les vraies données de l'API
  const { data: objetsData, isLoading, error } = useObjets({
    search: debouncedSearch,
    categorie: filters.category,
    sousCategorie: filters.sousCategorie,
    etat: filters.status,
    couleurPrincipale: filters.couleurPrincipale,
    couleurSecondaire: filters.couleurSecondaire,
    besoinAchat: filters.besoinAchat === 'true' ? true : filters.besoinAchat === 'false' ? false : undefined,
    sansPhoto: filters.sansPhoto === 'true' ? true : filters.sansPhoto === 'false' ? false : undefined,
    sansLieu: filters.sansLieu === 'true' ? true : undefined,
    ficheStatut:
      filters.ficheStatut === 'brouillon' || filters.ficheStatut === 'valide'
        ? filters.ficheStatut
        : undefined,
    page: currentPage,
    limit: itemsPerPage,
  });

  // Hooks pour les mutations
  const updateObjetMutation = useUpdateObjet();
  const createObjetMutation = useCreateObjet();
  const syncPhotosMutation = useSyncObjetPhotos();
  const deleteObjetMutation = useDeleteObjet();

  const objets = objetsData?.objets || [];

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setCurrentPage(1); // Reset to first page when filters change
  };

  const handleAdvancedFilterChange = (name: string, value: string) => {
    setFilters(prev => ({ ...prev, [name]: value }));
    setCurrentPage(1); // Reset to first page when filters change
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (itemsPerPage: number) => {
    setItemsPerPage(itemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  const handleViewModeChange = (mode: InventaireViewMode) => {
    setViewMode(mode);
  };

  const renderListRow = (objet: any) => (
    <div
      key={objet.id}
      className={`flex items-start gap-3 p-4 bg-white rounded-lg shadow-sm border border-gray-200 cursor-pointer hover:shadow-md transition-shadow ${
        objet.besoinAchat ? 'border-l-4 border-l-orange-500 bg-orange-50' : ''
      }`}
    >
      <label className="flex-shrink-0 cursor-pointer mt-1">
        <input
          type="checkbox"
          checked={selectedIds.includes(objet.id)}
          onChange={() => handleToggleSelection(objet.id)}
          className="form-checkbox h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-2 focus:ring-blue-500"
          onClick={(e) => e.stopPropagation()}
        />
      </label>
      <div
        className={`flex-1 min-w-0 ${permissions.canEdit ? 'cursor-pointer' : ''}`}
        onClick={permissions.canEdit ? () => handleEditObjet(objet) : undefined}
      >
        <ObjetCardMobile
          objet={objet}
          viewMode="liste"
          onEdit={handleEditObjet}
          canEdit={permissions.canEdit}
          canDelete={permissions.canDelete}
          canArchive={permissions.canArchive}
          canMove={permissions.canMove}
        />
      </div>
    </div>
  );

  const renderCardItem = (objet: any, cardLayout: 'compact' | 'full') => (
    <div key={objet.id} className="relative">
      <label className="absolute top-2 left-2 z-10 cursor-pointer bg-white/90 backdrop-blur-sm rounded-md shadow-sm p-1.5 hover:bg-white transition-colors">
        <input
          type="checkbox"
          checked={selectedIds.includes(objet.id)}
          onChange={() => handleToggleSelection(objet.id)}
          className="form-checkbox h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-2 focus:ring-blue-500"
          onClick={(e) => e.stopPropagation()}
        />
      </label>
      <ObjetCardMobile
        objet={objet}
        viewMode="card"
        cardLayout={cardLayout}
        onEdit={handleEditObjet}
        canEdit={permissions.canEdit}
        canDelete={permissions.canDelete}
        canArchive={permissions.canArchive}
        canMove={permissions.canMove}
      />
    </div>
  );

  const handleResetFilters = () => {
    setFilters({
      search: '',
      category: '',
      sousCategorie: '',
      status: '',
      couleurPrincipale: '',
      couleurSecondaire: '',
      besoinAchat: '',
      sansPhoto: '',
      sansLieu: '',
      ficheStatut: '',
    });
    setInventoryMode(false);
    setSearchParams({});
    setCurrentPage(1);
  };

  const handleEditObjet = (objet: any) => {
    setEditingObjet(objet);
    const hasPhotos = (objet?.photoCount ?? objet?.photos?.length ?? 0) > 0;
    const hasLocation = !!(objet?.zoneId || objet?.boxId || objet?.pieceId);
    if (inventoryMode || filters.sansPhoto === 'true') {
      setInventoryMode(true);
      if (!hasPhotos) setEditInitialSection('photos');
      else if (!hasLocation) setEditInitialSection('localisation');
      else setEditInitialSection('localisation');
    } else {
      setEditInitialSection('infos');
    }
    setIsEditModalOpen(true);
  };

  const handleSaveObjet = async (
    updatedObjet: any,
    locationChanged: { hasChanged: boolean; originalZoneId?: string; originalBoxId?: string; originalPieceId?: string },
    explicitPhotos?: string[],
    saveOptions?: { photosModified?: boolean }
  ) => {
    setIsSavingObjet(true);

    try {
      const { 
        id, 
        createdAt, 
        updatedAt, 
        userId, 
        photos,
        zone,
        box,
        piece,
        user,
        inventorie,
        dateInventaire,
        inventorieParId,
        ...allData 
      } = updatedObjet;
      
      const generatedSku =
        allData.nom?.trim() && allData.categorie?.trim()
          ? generateSKU({
              categorie: allData.categorie,
              sousCategorie: allData.sousCategorie,
              nom: allData.nom,
              zoneId: allData.zoneId,
              boxId: allData.boxId,
              pieceId: allData.pieceId,
            })
          : null;

      const cleanData = {
        nom: allData.nom?.trim(),
        categorie: allData.categorie || undefined,
        sousCategorie: allData.sousCategorie || undefined,
        etat: allData.etat,
        quantite: parseInt(String(allData.quantite ?? 1), 10) || 0,
        besoinAchat: allData.besoinAchat === true || allData.besoinAchat === 'true',
        remarques: allData.remarques || null,
        sku: allData.sku?.trim() || generatedSku,
        couleurPrincipale: allData.couleurPrincipale || null,
        couleurSecondaire: allData.couleurSecondaire || null,
        zoneId: allData.zoneId ? String(allData.zoneId) : null,
        boxId: allData.boxId ? String(allData.boxId) : null,
        pieceId: allData.pieceId ? String(allData.pieceId) : null,
      };

      const photoSource = explicitPhotos ?? photos;
      const photoUrls = (photoSource || [])
        .map((photo: any) => photoPayloadForSave(photo))
        .filter(Boolean);

      const objectId = id != null && String(id).trim() !== '' ? String(id) : undefined;
      const isNewObject = !objectId;
      const shouldSyncPhotos = saveOptions?.photosModified === true;
      let savedId = objectId;

      if (isNewObject) {
        const created = await createObjetMutation.mutateAsync({
          ...cleanData,
          createdByUserId: user?.id,
          createdByName: user?.name || user?.email,
        });
        savedId = created?.id != null ? String(created.id) : undefined;
      } else {
        await updateObjetMutation.mutateAsync({
          id: objectId,
          data: cleanData,
        });
      }

      const needsPhotoSync = photoUrls.length > 0 && (isNewObject || shouldSyncPhotos);
      if (savedId && needsPhotoSync) {
        try {
          await syncPhotosMutation.mutateAsync({ id: savedId, photos: photoUrls });
        } catch (photoError: any) {
          console.error('Erreur lors de la sauvegarde des photos:', photoError);
          showWarning(
            'Photos non enregistrées',
            'L\'objet est enregistré, mais les photos n\'ont pas pu être sauvegardées. Réessayez depuis l\'onglet Photos.',
          );
          setEditingObjet({
            ...allData,
            id: savedId,
            photos: photoUrls,
          });
          return;
        }
      } else if (!savedId && photoUrls.length > 0) {
        showWarning(
          'Photos non enregistrées',
          'L\'objet est enregistré, mais l\'identifiant est manquant pour sauvegarder les photos. Rouvrez l\'objet et ajoutez-les.',
        );
        return;
      }

      setIsEditModalOpen(false);
      setEditingObjet(null);
      setSortBy('recent');
      setCurrentPage(1);

      if (locationChanged.hasChanged && permissions.canMove && savedId) {
        void createMovement
          .mutateAsync({
            itemId: Number(savedId),
            ancienneZoneId: locationChanged.originalZoneId ? parseInt(String(locationChanged.originalZoneId)) : undefined,
            nouvelleZoneId: allData.zoneId ? parseInt(String(allData.zoneId)) : undefined,
            ancienneBoxId: locationChanged.originalBoxId ? parseInt(String(locationChanged.originalBoxId)) : undefined,
            nouvelleBoxId: allData.boxId ? parseInt(String(allData.boxId)) : undefined,
            utilisateurId: user?.id,
            utilisateurNom: user?.name,
            source: 'UI',
          })
          .then(() => console.log('✅ Mouvement enregistré avec succès'))
          .catch((movementError) => console.error('❌ Erreur lors de l\'enregistrement du mouvement:', movementError));
      }
    } catch (error: any) {
      console.error('Erreur lors de la sauvegarde:', error);
      console.error('Détails de l\'erreur:', error.response?.data);
      const apiMessage = error?.response?.data?.message;
      const detail = Array.isArray(apiMessage)
        ? apiMessage.join(', ')
        : typeof apiMessage === 'string'
          ? apiMessage
          : error?.message;
      showError(
        'Erreur de sauvegarde',
        detail ? `Impossible d'enregistrer l'objet : ${detail}` : "Impossible d'enregistrer l'objet.",
      );
    } finally {
      setIsSavingObjet(false);
    }
  };







  // Fonction pour supprimer un objet
  const handleDeleteObjet = async (objetId: string) => {
    try {
      await deleteObjetMutation.mutateAsync(objetId);
      showSuccess('Objet supprimé', 'L\'objet a été supprimé avec succès.');
      setIsEditModalOpen(false);
      setEditingObjet(null);
    } catch (error: any) {
      console.error('Erreur lors de la suppression:', error);
      const message = error.response?.data?.message || error.message || 'Erreur inconnue';
      showError('Erreur de suppression', message);
    }
  };

  // Fonction pour le déplacement d'un objet
  const handleMoveObjet = (objet: any) => {
    setMovingObjet(objet);
    setIsMoveModalOpen(true);
  };

  const handleMoveObjetConfirm = async (objetId: string, newZoneId: string, newBoxId: string) => {
    try {
      // Ici, on devrait appeler une API pour déplacer l'objet
      // Pour l'instant, on simule avec une mise à jour
      await updateObjetMutation.mutateAsync({
        id: objetId,
        data: {
          zoneId: newZoneId || null,
          boxId: newBoxId || null
        }
      });
      showSuccess('Objet déplacé', 'Le déplacement a été enregistré.');
    } catch (error) {
      console.error('Erreur lors du déplacement:', error);
      showError('Erreur de déplacement', 'Impossible de déplacer l\'objet.');
    }
  };

  const openRecognitionFlow = (photo: string) => {
    if (!photo?.trim()) return;
    stashPendingRecognitionPhoto(photo);
    setRecognitionPhoto(photo);
  };

  const closeRecognitionFlow = () => {
    clearPendingRecognitionPhoto();
    setRecognitionPhoto(null);
  };

  // Fonctions pour la création d'objets
  const handleCreateFromPhoto = (photo: string) => {
    openRecognitionFlow(photo);
  };

  const handleCreateManual = () => {
    // Ouvrir le modal d'édition avec un objet vide
    setEditingObjet(null);
    setIsEditModalOpen(true);
  };

  const handleImageRecognitionConfirm = (suggestion: any) => {
    const photo = recognitionPhoto;
    setImageSuggestion(suggestion);
    clearPendingRecognitionPhoto();
    setRecognitionPhoto(null);
    const newObjet = {
      nom: suggestion.nom || '',
      categorie: suggestion.categorie || '',
      sousCategorie: suggestion.sousCategorie || '',
      quantite: 1,
      etat: 'BON',
      besoinAchat: false,
      photos: photo ? [photo] : [],
      remarques: suggestion.description || '',
      sku: '',
      couleurPrincipale: '',
      couleurSecondaire: '',
      zoneId: '',
      boxId: '',
    };
    setEditingObjet(newObjet);
    setEditInitialSection('infos');
    setIsEditModalOpen(true);
  };

  // Handlers pour les opérations en masse
  const handleToggleSelection = (objetId: string) => {
    setSelectedIds(prev =>
      prev.includes(objetId)
        ? prev.filter(id => id !== objetId)
        : [...prev, objetId]
    );
  };

  const handleSelectAll = () => {
    const allIds = objets.map((objet: any) => objet.id);
    setSelectedIds(allIds);
  };

  const handleClearSelection = () => {
    setSelectedIds([]);
  };

  const handleBulkUpdate = () => {
    setIsBulkUpdateOpen(true);
  };

  const handleBulkMove = () => {
    setIsBulkMoveOpen(true);
  };

  const handleBulkDelete = () => {
    setIsBulkDeleteOpen(true);
  };

  const handleBulkInventory = () => {
    setIsBulkInventoryOpen(true);
  };

  // Fermeture des modals bulk avec nettoyage de la sélection
  const handleCloseBulkModal = () => {
    setIsBulkUpdateOpen(false);
    setIsBulkMoveOpen(false);
    setIsBulkDeleteOpen(false);
    setIsBulkInventoryOpen(false);
    setSelectedIds([]);
  };

  // Reprise après retour caméra mobile (rechargement partiel)
  useEffect(() => {
    const pending = takePendingRecognitionPhoto();
    if (pending) {
      setRecognitionPhoto(pending);
    }
  }, []);

  // Dashboard / navigation avec photo → reconnaissance IA d'abord (pas le formulaire direct)
  useEffect(() => {
    const state = location.state as { openCreateForm?: boolean; photo?: string } | null;
    if (!state?.openCreateForm) return;

    const photo = state.photo?.trim();
    if (photo) {
      openRecognitionFlow(photo);
    } else {
      setEditingObjet(null);
      setEditInitialSection('infos');
      setIsEditModalOpen(true);
    }
    window.history.replaceState({}, document.title);
  }, [location.state]);

  // Tri et groupement des objets
  const sortedObjets = sortItems(objets, sortBy);
  const groupedObjets = groupItems(sortedObjets, groupBy);

  // Vérifier s'il y a un filtre actif depuis l'URL
  const bulkPhotoLieuEnabled = isBulkPhotoLieuEnabled();
  const hasActiveFilter = searchParams.get('filter') !== null;
  const totalItems = objetsData?.total || 0;
  const totalPages = Math.ceil(totalItems / itemsPerPage);


  return (
    <div className="space-y-4">
      {/* Header mobile optimisé */}
      <MobileHeader
        title="Inventaire"
        subtitle={
          inventoryMode || filters.sansPhoto === 'true'
            ? '📋 Inventaire du jour — photo, état et lieu'
            : filters.ficheStatut === 'brouillon'
              ? '📝 Fiches à compléter (brouillon)'
              : hasActiveFilter
              ? '🔍 Filtre actif depuis le tableau de bord'
              : undefined
        }
        totalItems={totalItems}
        showFilters={showFilters}
        onToggleFilters={() => setShowFilters(!showFilters)}
      />

      {(filters.sansPhoto === 'true' || filters.sansLieu === 'true' || filters.ficheStatut === 'brouillon') && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm text-blue-900">
            {filters.ficheStatut === 'brouillon' &&
              `${totalItems} fiche(s) à compléter — renseignez nom, catégorie et validez la fiche.`}
            {filters.ficheStatut === 'brouillon' && (filters.sansPhoto === 'true' || filters.sansLieu === 'true') && ' '}
            {filters.sansPhoto === 'true' && `${totalItems} objet(s) sans photo — ouvrez chaque fiche pour photographier et localiser.`}
            {filters.sansPhoto === 'true' && filters.sansLieu === 'true' && ' '}
            {filters.sansLieu === 'true' && 'Filtre « sans lieu » actif.'}
          </p>
          <button
            type="button"
            onClick={handleResetFilters}
            className="text-sm font-medium text-blue-700 hover:text-blue-900 underline"
          >
            Réinitialiser les filtres
          </button>
        </div>
      )}

      {/* Contrôles de vue - version mobile */}
      <div className="bg-white shadow-sm border border-gray-200 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <ViewModeControls 
            viewMode={viewMode} 
            onViewModeChange={handleViewModeChange} 
          />
          {permissions.canCreate && (
            <CreateObjectButton 
              onCreateFromPhoto={handleCreateFromPhoto}
              onCreateManual={handleCreateManual}
            />
          )}
        </div>
      </div>

      {/* Recherche mobile */}
      <MobileSearch
        value={filters.search}
        onChange={(value) => handleFilterChange({ target: { name: 'search', value } })}
        placeholder="Rechercher par nom ou SKU..."
      />

      {/* Filtres avancés */}
                {showFilters && (
                  <AdvancedFilters
                    filters={filters}
                    onFilterChange={handleAdvancedFilterChange}
                    onResetFilters={handleResetFilters}
                    showFicheStatutFilter={bulkPhotoLieuEnabled}
                  />
                )}

      {/* Tri et groupement */}
      <SortGroupControls
        sortBy={sortBy}
        groupBy={groupBy}
        onSortChange={setSortBy}
        onGroupChange={setGroupBy}
        type="objets"
      />

      {/* Sélection et actions en masse - seulement si permissions */}
      {(permissions.canBulkEdit || permissions.canBulkDelete || permissions.canBulkMove || permissions.canBulkInventory) && (
        <SelectionControls
          selectedIds={selectedIds}
          totalCount={totalItems}
          onSelectAll={handleSelectAll}
          onClearSelection={handleClearSelection}
        />
      )}

      {(permissions.canBulkEdit || permissions.canBulkDelete || permissions.canBulkMove || permissions.canBulkInventory) && (
        <BulkActions
          selectedIds={selectedIds}
          onUpdate={permissions.canBulkEdit ? handleBulkUpdate : undefined}
          onMove={permissions.canBulkMove ? handleBulkMove : undefined}
          onDelete={permissions.canBulkDelete ? handleBulkDelete : undefined}
          onInventory={permissions.canBulkInventory ? handleBulkInventory : undefined}
        />
      )}

      {/* Liste des objets */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-gray-500 mt-2">Chargement des objets...</p>
          </div>
        ) : error ? (
          <div className="p-8 text-center">
            <p className="text-red-600">Erreur: {error.message}</p>
          </div>
        ) : objets.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-gray-500">Aucun objet trouvé.</p>
            <button
              onClick={handleResetFilters}
              className="mt-2 text-blue-600 hover:text-blue-800 text-sm font-medium"
            >
              Réinitialiser les filtres
            </button>
          </div>
        ) : (
          <>
            {/* Liste des objets avec groupement */}
            {groupedObjets.map((group) => (
              <div key={group.key} className="space-y-4">
                {groupBy !== 'none' && (
                  <h3 className="text-lg font-semibold text-gray-900 bg-gray-100 px-4 py-2 rounded-md">
                    {group.label}
                    <span className="ml-2 text-sm text-gray-500">
                      ({group.count} objet{group.count > 1 ? 's' : ''})
                    </span>
                  </h3>
                )}
                
                {viewMode === 'liste' ? (
                  <>
                    <div className="hidden md:block">
                      <ObjetListTable
                        objets={group.items}
                        selectedIds={selectedIds}
                        onToggleSelection={handleToggleSelection}
                        onEdit={handleEditObjet}
                        canEdit={permissions.canEdit}
                      />
                    </div>
                    <div className="md:hidden space-y-3">{group.items.map(renderListRow)}</div>
                  </>
                ) : (
                  <ResponsiveGrid viewMode={viewMode}>
                    {group.items.map((objet: any) =>
                      renderCardItem(objet, viewMode === 'card-full' ? 'full' : 'compact'),
                    )}
                  </ResponsiveGrid>
                )}
              </div>
            ))}

            {/* Contrôles de pagination */}
            {totalPages > 1 && (
              <PaginationControls
                currentPage={currentPage}
                totalPages={totalPages}
                itemsPerPage={itemsPerPage}
                totalItems={totalItems}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
              />
            )}
          </>
        )}
      </div>

                {isEditModalOpen && (
                  <ObjetEditModal
                    objet={editingObjet}
                    isOpen={isEditModalOpen}
                    onClose={() => {
                      setIsEditModalOpen(false);
                      setEditingObjet(null);
                    }}
                    onSave={handleSaveObjet}
                    onDelete={handleDeleteObjet}
                    initialSection={editInitialSection}
                    inventoryMode={inventoryMode || filters.sansPhoto === 'true'}
                    isSaving={isSavingObjet}
                  />
                )}

                {isMoveModalOpen && (
                  <MoveObjetModal
                    objet={movingObjet}
                    isOpen={isMoveModalOpen}
                    onClose={() => setIsMoveModalOpen(false)}
                    onMove={handleMoveObjetConfirm}
                  />
                )}

                {recognitionPhoto && (
                  <ImageRecognitionModal
                    isOpen
                    onClose={closeRecognitionFlow}
                    imageData={recognitionPhoto}
                    onConfirm={handleImageRecognitionConfirm}
                  />
                )}

                {/* Modals pour opérations en masse */}
                <BulkUpdateModal
                  isOpen={isBulkUpdateOpen}
                  onClose={handleCloseBulkModal}
                  selectedIds={selectedIds}
                />

                <BulkMoveModal
                  isOpen={isBulkMoveOpen}
                  onClose={handleCloseBulkModal}
                  selectedIds={selectedIds}
                />

                <BulkDeleteModal
                  isOpen={isBulkDeleteOpen}
                  onClose={handleCloseBulkModal}
                  selectedIds={selectedIds}
                />

                <BulkInventoryModal
                  isOpen={isBulkInventoryOpen}
                  onClose={handleCloseBulkModal}
                  selectedIds={selectedIds}
                />
              </div>
            );
          };

          export default InventairePage;
